import { HttpParams } from '@angular/common/http';
import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthUserInfo, IAuthService } from '@core-features/core';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { jwtDecode } from 'jwt-decode';
import {
  BehaviorSubject,
  filter,
  firstValueFrom,
  from,
  map,
  Observable,
  ReplaySubject,
} from 'rxjs';
import {
  AuthErrorPresenter,
  AuthHostServiceConfig,
  AuthLogger,
} from './models';
import {
  AUTH_HOST_ERROR_PRESENTER,
  AUTH_HOST_LOGGER,
  AUTH_HOST_SERVICE_CONFIG,
} from './tokens';
import { OidcConfig } from './utils';

@Injectable()
export class AuthHostService implements IAuthService {
  // #region DI
  private readonly _authLoggerService: AuthLogger = inject(AUTH_HOST_LOGGER);
  private readonly _authErrorPresenter: AuthErrorPresenter = inject(
    AUTH_HOST_ERROR_PRESENTER,
  );
  private readonly _authServiceConfig: AuthHostServiceConfig = inject(
    AUTH_HOST_SERVICE_CONFIG,
  );

  private readonly _ngZone: NgZone = inject(NgZone);
  private readonly _router: Router = inject(Router);
  private readonly _oauthService: OAuthService = inject(OAuthService);
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);
  // #endregion

  private readonly _state$ = new BehaviorSubject<
    'authenticating' | 'authenticated' | 'unauthenticated'
  >('authenticating');
  private readonly _userInfo$ = new ReplaySubject<AuthUserInfo | undefined>(1);
  private readonly _isTriggeredSilentRenewRunning$ =
    new BehaviorSubject<boolean>(false);

  private readonly _signOutRoute = '/special/signout';
  private readonly _callbackRoute = '/callback';
  private readonly _checkSessionIframeRefreshMs = 60000;
  private _checkSessionIframeRefreshIntervalId: number | undefined;
  private _lastCheckSessionIframeRefresh = 0;

  readonly isAuthenticated$: Observable<boolean> = this._state$.pipe(
    filter((state) => ['authenticated', 'unauthenticated'].includes(state)),
    map((state) => state === 'authenticated'),
  );
  readonly userInfo$: Observable<AuthUserInfo | undefined> =
    this._userInfo$.asObservable();

  readonly $isAuthenticated = toSignal(this.isAuthenticated$, {
    initialValue: false,
  });

  readonly $userInfo = toSignal(this.userInfo$);

  /**
   * Stores url path which user want's to visit before he is redirected to IdentityServer login page.
   * After he is succesfully logged in, then we will use this value and redirect user there
   */
  private get _redirectPathStorageKey() {
    return this._authServiceConfig.oidcClientId + '_redirect';
  }

  constructor() {
    // Init moved to external initialization to avoid circular dependency
    // init() will be called via APP_INITIALIZER or provideEnvironmentInitializer
  }
  // #region private methods
  /**
   * Internal method to set authentication status
   * @param authenticated
   */
  private _setAuthenticated(authenticated: boolean): void {
    this._state$.next(authenticated ? 'authenticated' : 'unauthenticated');
    this._setCheckSessionIframeRefreshInterval(authenticated);
  }

  private _setCheckSessionIframeRefreshInterval(authenticated: boolean): void {
    clearInterval(this._checkSessionIframeRefreshIntervalId);
    document.removeEventListener(
      'visibilitychange',
      this._refreshCheckSessionIframeIfStale,
    );

    if (!authenticated) {
      this._checkSessionIframeRefreshIntervalId = undefined;
      return;
    }

    // the library has just re-created the iframe for the received token
    this._lastCheckSessionIframeRefresh = Date.now();
    // browsers without the Page Visibility API have no visibilityState and keep refreshing every minute
    this._checkSessionIframeRefreshIntervalId = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') {
        this._refreshCheckSessionIframe();
      }
    }, this._checkSessionIframeRefreshMs);
    document.addEventListener(
      'visibilitychange',
      this._refreshCheckSessionIframeIfStale,
    );
  }

  private readonly _refreshCheckSessionIframeIfStale = (): void => {
    const isStale =
      Date.now() - this._lastCheckSessionIframeRefresh >=
      this._checkSessionIframeRefreshMs;
    if (document.visibilityState === 'visible' && isStale) {
      this._refreshCheckSessionIframe();
    }
  };

  // the checksession response can update or remove the IdentityServer session cookie
  // (deleted user, changed roles, expired login), which the iframe then reports as "changed"
  private _refreshCheckSessionIframe(): void {
    this._lastCheckSessionIframeRefresh = Date.now();
    this._oauthService.restartSessionChecksIfStillLoggedIn();
  }

  private _setRedirectPath(path: string): void {
    if (path !== this._callbackRoute && path !== this._signOutRoute) {
      sessionStorage.setItem(this._redirectPathStorageKey, path);
    }
  }

  /**
   * Gets initial tokens and user data with no visible redirect to authorization server.
   * For token renewal, use tryRefreshSession() instead.
   */
  private _loginWithSilentRenew(): Promise<boolean> {
    this._authLoggerService.logTrace('Initiate silent refresh.');
    return this._oauthService
      .silentRefresh()
      .then((event) => {
        if (event instanceof OAuthErrorEvent) {
          this._authLoggerService.logTrace(
            'Login with silent renew failed.',
            event,
          );
          this._setAuthenticated(false);
          return false;
        }
        return true;
      })
      .catch((error) => {
        this._authLoggerService.logTrace(
          'Login with silent renew failed.',
          error,
        );
        this._setAuthenticated(false);
        return false;
      });
  }

  private _setupOAuthServiceEventsHandling(): void {
    // get userinfo after a token is received
    this._oauthService.events
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((e) => e.type === 'token_received'),
      )
      .subscribe(() => {
        this._authLoggerService.logTrace('Token received.');
        this._getUserInfo(!this.$isAuthenticated);
      });

    // set up single sign out
    this._oauthService.events
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((e) => e.type === 'session_terminated'),
      )
      .subscribe(() => {
        this._authLoggerService.logTrace('Session terminated.');
        this.navigateToSignOutPage();
      });
  }

  private _getStateQueryParam(): string | null {
    const params = new HttpParams({ fromString: window.location.search });
    return params.get('state');
  }

  private _handleTryLoginError(
    error: OAuthErrorEvent | string,
    stateQueryParam: string | null,
  ): void {
    const errorMessage = 'Error from oauthService.tryLogin().';

    if (error === 'Token has expired') {
      this._authErrorPresenter.displayError(
        'general.error_code.sign_in_wrong_time',
      );

      const accessToken = this._oauthService.getAccessToken();
      const username: string = accessToken
        ? jwtDecode<{ username: string }>(accessToken).username
        : '';
      this._authLoggerService.logError(errorMessage, error, '', username);
    } else if (
      error instanceof OAuthErrorEvent &&
      error.type === 'invalid_nonce_in_state'
    ) {
      const nonceInStorage = sessionStorage.getItem('nonce');
      const detailText = `State query param invalid. Value from request: ${stateQueryParam}. Value in storage: ${nonceInStorage}`;
      this._authLoggerService.logError(errorMessage, error, detailText);
      // try to login again
      // see https://dev.azure.com/palfinger-swdev/Palfinger.Paldesk/_git/Palfinger.Paldesk.Dashboard/pullrequest/17267
      // and https://palfinger-swdev.visualstudio.com/DefaultCollection/Palfinger.Paldesk/_git/Palfinger.Paldesk.Dashboard/pullrequest/29714
      this._loginWithSilentRenew().then((success) => {
        if (success) {
          this._redirectToRequestedUrl();
        } else {
          this._authErrorPresenter.displayError();
        }
      });
    } else {
      this._authLoggerService.logError(errorMessage, error);
      this._authErrorPresenter.displayError();
    }
  }

  private _redirectToRequestedUrl(): void {
    const redirectPath = sessionStorage.getItem(this._redirectPathStorageKey);
    if (redirectPath) {
      this._authLoggerService.logTrace(
        `Redirecting user to path:${redirectPath}.`,
      );
      sessionStorage.removeItem(this._redirectPathStorageKey);
      this._router.navigateByUrl(redirectPath);
    } else {
      this._authLoggerService.logTrace('Redirecting user to root');
      this._router.navigate(['']);
    }
  }

  private _getUserInfo(displayErrors = true): void {
    this._oauthService
      .loadUserProfile()
      .then((response: any) => {
        this._authLoggerService.logTrace('User info loaded.');
        this._userInfo$.next(response.info as AuthUserInfo);
        this._setAuthenticated(true);
      })
      .catch((error) => {
        this._authLoggerService.logError('Error loading user info', error);
        if (displayErrors) {
          this._authErrorPresenter.displayError();
        }
      });
  }

  /**
   * Tries to refresh tokens and user info.
   * It is used, when auth interceptor detects that access token is expired.
   * If the refresh fails with 'login_required', navigates to sign-out page.
   * If another error occurs, retries once.
   * If it still fails, the request which triggered this method will continue with the old token.
   * @return A promise which resolves when the refresh is completed or failed.
   */
  private async _tryRefreshSession(): Promise<void> {
    const isSilentRenewRunning = await firstValueFrom(
      this._isTriggeredSilentRenewRunning$,
    );

    if (isSilentRenewRunning) {
      await firstValueFrom(
        this._isTriggeredSilentRenewRunning$.pipe(
          filter((isRunning) => !isRunning),
        ),
      );
    } else {
      this._isTriggeredSilentRenewRunning$.next(true);
      this._authLoggerService.logTrace('Start refresh login session.');
      let triggeredSilentRenewAttempt = 1;

      while (triggeredSilentRenewAttempt <= 2) {
        try {
          await this._oauthService.silentRefresh();
          break;
        } catch (error) {
          this._authLoggerService.logWarning('Silent refresh error.', error);

          // indicates that the IdentityServer auth cookie already expired
          const isLoginRequired =
            error instanceof OAuthErrorEvent &&
            error.type === 'silent_refresh_error' &&
            error.reason instanceof OAuthErrorEvent &&
            error.reason.params &&
            'error' in error.reason.params &&
            error.reason.params.error === 'login_required';

          if (isLoginRequired) {
            this.navigateToSignOutPage();
            // throwing an error to stop the http request which triggered this method
            throw new Error('login_required', { cause: error });
          }

          triggeredSilentRenewAttempt++;
        }
      }

      this._isTriggeredSilentRenewRunning$.next(false);
    }
  }

  // Re-fetches the profile from identity: a successful silent refresh raises
  // `token_received` → `_getUserInfo()` → fresh immutable user via `userInfo$`.
  refreshUserInfo(): Observable<boolean> {
    return from(this._tryRefreshSession()).pipe(map(() => true));
  }

  init() {
    this._oauthService.configure(
      OidcConfig.Configuration(this._authServiceConfig),
    );

    this._setupOAuthServiceEventsHandling();

    // get tokens if this is redirect back from identity server,
    // initiate silent refresh otherwise
    this.requestTokensIfRedirectFromAuthorizationServer().then((success) => {
      if (!success) {
        return;
      }

      if (window.location.pathname === this._callbackRoute) {
        this._redirectToRequestedUrl();
      } else {
        this._loginWithSilentRenew();
      }
    });

    this._oauthService.setupAutomaticSilentRefresh();
  }
  // #endregion

  isAccessTokenValid(): boolean {
    return this._oauthService.hasValidAccessToken();
  }

  async getAccessToken(): Promise<string> {
    const accessToken = this._oauthService.getAccessToken();

    const shouldRefreshToken =
      accessToken &&
      Date.now() > this._oauthService.getAccessTokenExpiration() - 1000 * 60;

    if (shouldRefreshToken) {
      return this._tryRefreshSession().then(() =>
        this._oauthService.getAccessToken(),
      );
    }

    return accessToken;
  }

  async requestTokensIfRedirectFromAuthorizationServer(): Promise<boolean> {
    // state query param value is copied to a local variable,
    // because the query string is removed from url by oauthService.tryLogin()
    const stateQueryParam = this._getStateQueryParam();

    return this._oauthService.tryLogin().catch((error) => {
      this._handleTryLoginError(error, stateQueryParam);
      return false;
    });
  }

  login() {
    this._authLoggerService.logTrace('Start login.');
    this._setRedirectPath(window.location.pathname + window.location.search);
    this._oauthService.initCodeFlow();
  }

  /**
   * Removes tokens and user data from browser storage without sending anything to Identity Server.
   *
   * It can be used when a user is not logged-in in Identity Server,
   * but user data is found in the storage.
   */
  logoutLocally(): void {
    this._oauthService.logOut(true);
    this._setAuthenticated(false);
  }

  logout(): void {
    this._authLoggerService.logTrace('Start logoff.');
    sessionStorage.removeItem(this._redirectPathStorageKey);
    this._oauthService.logOut();
    this._setAuthenticated(false);
  }

  navigateToProfile(): void {
    window.open(
      this._authServiceConfig.securityTokenService + '/profile',
      '_blank',
    );
  }

  navigateToSignOutPage() {
    const location = window.location.pathname + window.location.search;
    this._authLoggerService.logTrace(
      `Attempting navigation to sign-out page from: ${location}`,
    );

    if (location !== this._signOutRoute) {
      this._setRedirectPath(location);

      this._ngZone.run(() => {
        this._router.navigate([this._signOutRoute]).catch((error) => {
          this._authLoggerService.logTrace(
            'Navigation to sign-out page failed:',
            error,
          );
        });
      });
    }
  }
}
